import apiClient from 'services/api/apiService';
import {
  IConfigurationQuestion,
  IConfigurationQuestionOption,
  ICreateQuestionData,
  IUpdateQuestionData,
  IReorderQuestionsData,
  ICreateOptionData,
  IUpdateOptionData,
  IReorderOptionsData,
} from 'types/ConfigurationQuestion.types';
import { ITestExtraPriceQuestion } from 'types/Configuration.types';

const CONFIGURATIONS_URL = '/v1/configurations';
const QUESTIONS_URL = '/v1/questions';

// ---------------------------------------------------------------------------
// Questions (nested under a configuration)
// ---------------------------------------------------------------------------

export const getConfigurationQuestions = async (
  configurationId: number
): Promise<IConfigurationQuestion[]> => {
  const { data } = await apiClient.get<any>(
    `${CONFIGURATIONS_URL}/${configurationId}/questions`
  );
  return data.data;
};

export const getConfigurationQuestion = async (
  configurationId: number,
  questionId: number
): Promise<IConfigurationQuestion> => {
  const { data } = await apiClient.get<any>(
    `${CONFIGURATIONS_URL}/${configurationId}/questions/${questionId}`
  );
  return data.data;
};

export const createConfigurationQuestion = async (
  configurationId: number,
  createQuestionData: ICreateQuestionData
): Promise<IConfigurationQuestion> => {
  const { data } = await apiClient.post<any>(
    `${CONFIGURATIONS_URL}/${configurationId}/questions`,
    createQuestionData
  );
  return data.data;
};

export const updateConfigurationQuestion = async (
  configurationId: number,
  questionId: number,
  updateQuestionData: IUpdateQuestionData
): Promise<IConfigurationQuestion> => {
  const { data } = await apiClient.patch<any>(
    `${CONFIGURATIONS_URL}/${configurationId}/questions/${questionId}`,
    updateQuestionData
  );
  return data.data;
};

export const reorderConfigurationQuestions = async (
  configurationId: number,
  reorderData: IReorderQuestionsData
): Promise<any> => {
  const { data } = await apiClient.patch<any>(
    `${CONFIGURATIONS_URL}/${configurationId}/questions/reorder`,
    reorderData
  );
  return data.data;
};

export const deleteConfigurationQuestion = async (
  configurationId: number,
  questionId: number
): Promise<any> => {
  const { data } = await apiClient.delete<any>(
    `${CONFIGURATIONS_URL}/${configurationId}/questions/${questionId}`
  );
  return data.data;
};

// Bulk-replaces a configuration's questions and options. Used by the "Apply"
// button in the test configuration modal to persist the locally-edited questions.
export const putConfigurationQuestions = async (
  configurationId: number,
  putData: { extra_price_questions: ITestExtraPriceQuestion[] }
): Promise<any> => {
  const { data } = await apiClient.put<any>(
    `${CONFIGURATIONS_URL}/${configurationId}/questions`,
    putData
  );
  return data.data;
};

// ---------------------------------------------------------------------------
// Options (nested under a question)
// ---------------------------------------------------------------------------

export const getQuestionOptions = async (
  questionId: number
): Promise<IConfigurationQuestionOption[]> => {
  const { data } = await apiClient.get<any>(
    `${QUESTIONS_URL}/${questionId}/options`
  );
  return data.data;
};

export const getQuestionOption = async (
  questionId: number,
  optionId: number
): Promise<IConfigurationQuestionOption> => {
  const { data } = await apiClient.get<any>(
    `${QUESTIONS_URL}/${questionId}/options/${optionId}`
  );
  return data.data;
};

export const createQuestionOption = async (
  questionId: number,
  createOptionData: ICreateOptionData
): Promise<IConfigurationQuestionOption> => {
  const { data } = await apiClient.post<any>(
    `${QUESTIONS_URL}/${questionId}/options`,
    createOptionData
  );
  return data.data;
};

export const updateQuestionOption = async (
  questionId: number,
  optionId: number,
  updateOptionData: IUpdateOptionData
): Promise<IConfigurationQuestionOption> => {
  const { data } = await apiClient.patch<any>(
    `${QUESTIONS_URL}/${questionId}/options/${optionId}`,
    updateOptionData
  );
  return data.data;
};

export const reorderQuestionOptions = async (
  questionId: number,
  reorderData: IReorderOptionsData
): Promise<any> => {
  const { data } = await apiClient.patch<any>(
    `${QUESTIONS_URL}/${questionId}/options/reorder`,
    reorderData
  );
  return data.data;
};

export const deleteQuestionOption = async (
  questionId: number,
  optionId: number
): Promise<any> => {
  const { data } = await apiClient.delete<any>(
    `${QUESTIONS_URL}/${questionId}/options/${optionId}`
  );
  return data.data;
};
